import type { IChannel } from "containers/Channels/types";
import { resolveCalculationBaseColumn } from "containers/TermFormContainer/ChannelsStep/calculationBaseColumnUtils";
import {
    getLegacyMilestoneBaseColumn,
    resolveMilestoneBaseColumn,
} from "containers/TermFormContainer/ChannelsStep/milestoneBaseColumnUtils";
import type { ChannelParams } from "containers/TermFormContainer";
import { splitDefaultConfigurationFlag } from "containers/TermFormContainer/utils";
import type { TemplateConfiguration } from "store/termTemplatesStore";

type GetTemplateChannelsOptions = {
    /**
     * When compensation is off, formulas stay standalone even if the channel belongs to a group.
     */
    usesCompensation?: boolean;
};

/**
 * Channels of a template with milestones in display units, bases resolved, and the live compensation group.
 * Old JSON has no calculation / milestone columns: thresholds stay in the unit they were saved in
 * (cents only on plain CA channels). Format overrides keep their formatTypeId; a missing one is the default formula.
 */
export const getTemplateChannels = (
    templateConfiguration: TemplateConfiguration | undefined,
    channels: IChannel[],
    { usesCompensation = true }: GetTemplateChannelsOptions = {},
): ChannelParams[] | undefined => {
    const channelById = new Map(channels.map((channel) => [channel.id, channel]));

    return templateConfiguration?.channels?.map((channel) => {
        const currentChannel = channelById.get(channel.channelId);
        const calculationBaseColumn = currentChannel
            ? resolveCalculationBaseColumn(currentChannel, channel.calculationBaseColumn)
            : channel.calculationBaseColumn;
        const legacyMilestone = getLegacyMilestoneBaseColumn(currentChannel);
        const milestoneBaseColumn = resolveMilestoneBaseColumn(
            channel.milestoneBaseColumn ?? legacyMilestone,
            calculationBaseColumn ?? legacyMilestone,
        );
        const channelTypeMultiplier = milestoneBaseColumn === "quantity" ? 1 : 100;
        const channelData = Array.isArray(channel.data) ? channel.data : JSON.parse(channel.data as string);
        const { milestones, showDefaultConfiguration } = splitDefaultConfigurationFlag(
            channelData,
            channel.showDefaultConfiguration,
        );
        const parsedData = milestones.map((milestone) => ({
            ...milestone,
            quantity: +milestone.quantity / channelTypeMultiplier,
        }));
        const groupId = usesCompensation
            ? currentChannel
                ? currentChannel.compensationGroupId ?? undefined
                : channel.groupId
            : -1;

        return {
            ...channel,
            data: parsedData,
            calculationBaseColumn,
            milestoneBaseColumn,
            formatTypeId: channel.formatTypeId ?? null,
            groupId,
            showDefaultConfiguration,
        };
    });
};
